import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation, useQuery } from "react-query";
import {
  destroyInvite,
  loadInvites,
  resendInvite,
} from "../../../queries/invite";
import ReactPaginate from "react-paginate";
import { useErrorHandler } from "react-error-boundary";
import {
  removeItem,
  updateObjectInArray,
} from "../../../redux/reducers/helpers";
import InviteModal from "./InviteModal";
import isEmpty from "lodash/isEmpty";
import ResendInviteModal from "../../common/ResendInviteModal";
import DeleteInviteModal from "../../common/DeleteInviteModal";

const InvitesPage = ({ queryClient, toast }) => {
  const handleError = useErrorHandler();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [modal, setModal] = useState(false);

  const fetchInvite = async (page = 1) =>
    await loadInvites(page)
      .then((data) => {
        setTotalPages(data?.meta?.total_pages || 0);
        setPage(data?.meta?.page || 1);
        return data.results;
      })
      .catch((error) => {
        handleError(error);
      });

  const {
    isLoading,
    isError,
    data: invites,
    isPreviousData,
  } = useQuery(
    ["invites", page],
    () =>
      fetchInvite(page).then((data) => {
        return data;
      }),
    {
      keepPreviousData: true,
    }
  );

  const deleteInvite = useMutation(async (invite) => {
    return destroyInvite(invite.id).then((data) => {
      queryClient.setQueryData(["invites", page], removeItem(invites, data));
    });
  });

  const sendInvite = useMutation(async (invite) => {
    return await resendInvite(invite.id).then((data) => {
      queryClient.setQueryData(
        ["invites", page],
        updateObjectInArray(invites, data)
      );
      toast.success([
        {
          title: "Invitation has been send",
          body: invite.email,
        },
      ]);
    });
  });

  const handlePageClick = (e) => {
    if (!isPreviousData) {
      setPage(e.selected + 1);
    }
  };
  const toggle = () => setModal(!modal);
  return (
    <div className="container">
      <div className="d-flex align-items-center mb-5">
        <h3 className="m-0">Invites</h3>
        <a
          className="btn btn-sm btn-main ml-auto"
          data-toggle="modal"
          data-target="#invitation-modal"
          onClick={toggle}
        >
          Invite users
        </a>
      </div>

      <table className="table-list">
        <thead>
          <tr>
            <th>FULL NAME / EMAIL</th>
            <th>INVITATION DATE</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {!isEmpty(invites) && !isLoading && !isError && (
            <>
              {invites.map((invite) => (
                <tr key={invite.id} className="text-small">
                  <td>
                    <h5 className="mb-1">
                      <a href={`mailto:${invite.email}`}>{invite.email}</a>{" "}
                      <small className="d-inline-block text-small text-link-alt">
                        Invitation {invite.status}
                      </small>
                    </h5>
                  </td>
                  <td>{invite.invitationDate}</td>
                  <td className="table-actions nowrap text-md-center">
                    <a
                      className="btn-icon btn-transparent ml-n2 ml-md-0"
                      data-placement="top"
                      title="Resend invite"
                      data-tooltip="tooltip"
                      data-toggle="modal"
                      data-target={`#resend-invite-modal-${invite.id}`}
                    >
                      <i className="icon-envelope" />
                    </a>
                    <a
                      className="btn-icon btn-transparent"
                      data-tooltip="tooltip"
                      data-placement="top"
                      title="Cancel invite"
                      data-toggle="modal"
                      data-target={`#delete-invite-modal-${invite.id}`}
                    >
                      <i className="icon-cancel" />
                    </a>
                    <DeleteInviteModal
                      deleteMethod={deleteInvite}
                      id={invite.id}
                      fullName={invite.email}
                      entity={"Invite"}
                    />
                    <ResendInviteModal
                      inviteMethod={sendInvite}
                      id={invite.id}
                      email={invite.email}
                    />
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
      {totalPages > 1 && !isLoading && (
        <ReactPaginate
          onPageChange={(e) => handlePageClick(e)}
          previousLabel={"<"}
          nextLabel={">"}
          breakLabel={"..."}
          pageCount={totalPages}
          pageRangeDisplayed={totalPages}
          marginPagesDisplayed={2}
          containerClassName={"pagination mt-45"}
          activeClassName={"active"}
          activeLinkClassName={"current"}
        />
      )}
      <InviteModal
        queryDataString={["invites", page]}
        modal={modal}
        toggle={toggle}
        handleError={handleError}
        queryClient={queryClient}
      />
    </div>
  );
};

InvitesPage.propTypes = {
  queryClient: PropTypes.object.isRequired,
};

export default InvitesPage;
